import { For, JSX } from 'solid-js';
import { TrbToolbar } from './TrbToolbar';
import { UvIconButton } from '../button/UvIconButton';

/**
 * A single tool entry rendered by {@link EditingToolbar}. Generic over the tool
 * identifier type so callers can use their own enum (e.g. a web3d line-editing
 * tool enum) without coupling this component to any domain. The array of items
 * also acts as the enabled-tools subset (only listed tools are shown).
 * `iconName` follows the same convention as {@link UvIconButton}: a `tc-icon-*`
 * class renders the Trimble Connect icon font, any other value is treated as a
 * modus icon name.
 */
export interface EditingToolbarItem<T extends string = string> {
  readonly tool: T;
  readonly iconName: string;
  readonly tooltip: string;
}

export interface EditingToolbarProps<T extends string = string> {
  readonly tools: readonly EditingToolbarItem<T>[];
  readonly activeTool: T | undefined;
  /** Disabled when no tool may be selected (e.g. no active layer). */
  readonly disabled: boolean;
  onToggleTool(tool: T): void;
}

/**
 * @intent Reusable vertical editing toolbar built from {@link TrbToolbar} and
 * {@link UvIconButton} (matching the toggle-button pattern used by
 * AlignmentNavigationToolbar). Each button toggles a tool; tools are mutually
 * exclusive and re-clicking the active tool deactivates it. Layout/positioning
 * and label resolution are the caller's responsibility.
 */
export function EditingToolbar<T extends string = string>(
  props: EditingToolbarProps<T>
): JSX.Element {
  return (
    <TrbToolbar orientation="vertical" addSeparators={false}>
      <For each={props.tools}>
        {(item) => (
          <UvIconButton
            dataTestId={`editing-tool-${item.tool}`}
            type="toggle"
            icon={item.iconName}
            tooltip={item.tooltip}
            pressed={props.activeTool === item.tool}
            disabled={props.disabled}
            onClick={() => props.onToggleTool(item.tool)}
          />
        )}
      </For>
    </TrbToolbar>
  );
}
